import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageHeader from "components/PageLayout";
import {
  ArrowLeft,
  FileText,
  Clock,
  MessageSquare,
  Plus,
  Settings,
  CreditCard,
  Building2,
  Cpu,
  Send,
} from "lucide-react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth, requireAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { useToast } from "components/Toast";
import RecordStatusPill from "components/Records/RecordStatusPill";
import RecordTimeline from "components/Records/RecordTimeline";
import { buildStatusTimeline } from "utilities/recordTimeline";
import {
  MID_APP_STATUS,
  MID_APP_TRANSITIONS,
  MID_APP_LABELS,
  statusLabelFrom,
  statusDotFrom,
  statusToneFrom,
} from "utilities/statusTones";
import { InfoField } from "components/Account/InfoField";
import Skeleton from "components/Skeleton";
import SourceBadge from "components/SourceBadge";
import EntityTicketsSection from "components/Tickets/EntityTicketsSection";
import RecordLinksPanel from "components/Records/RecordLinksPanel";
import ApplicationStatusDialog from "components/ApplicationStatusDialog";
import PendsPanel from "components/MidApplications/PendsPanel";
import StratusTransmitModal from "components/MidApplications/StratusTransmitModal";
import StratusTransmittedPanel from "components/MidApplications/StratusTransmittedPanel";
import { canShowTransmit } from "utilities/stratusPreflight";

// Transitions and labels come from utilities/statusTones — they used to be
// duplicated here, and that private copy was missed when the vocabulary gained
// out_for_signature / submitted / underwriting, so this dropdown silently kept
// offering the old six.
const MID_TRANSITIONS = MID_APP_TRANSITIONS;
const STATUS_LABELS = MID_APP_LABELS;

// SOUND-618: negative / terminal outcomes must carry an explanation, mirroring
// the registration tab's `requiresReason` convention. The API does not enforce
// this — it is a UI guard so the status history stays legible.
// SOUND-688: `pended` removed — it is no longer a status, so it can no longer
// be selected and can no longer require a reason. The pend itself carries the
// "why" now, on the pend row.
const REASON_REQUIRED_MID_STATUSES = new Set(["declined", "withdrawn"]);

function MidApplication() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const isAdmin = isInternal(user);
  const { toast } = useToast();

  const [application, setApplication] = useState(null);
  const [processors, setProcessors] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  // SOUND-618: "Update Status" now opens a confirmation dialog instead of firing
  // immediately. Reason + notification-suppression live inside the dialog, which
  // resets them on every open.
  const [confirmOpen, setConfirmOpen] = useState(false);
  // SOUND-776: the Kurv transmit dialog.
  const [transmitOpen, setTransmitOpen] = useState(false);

  // Fetch app + processors. `silent` skips the loading skeleton — the page
  // returns a skeleton while loading, which would unmount the transmit dialog
  // and throw away its result screen.
  const fetchData = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);

      const [appRes, processorsRes] = await Promise.all([
        makeApiRequest(
          "get",
          apiRoutes.getMidApplicationById(id),
          { id: user.id, accessToken: user.accessToken },
          effectiveCompanyId
        ),
        makeApiRequest(
          "get",
          apiRoutes.getProcessors,
          { id: user.id, accessToken: user.accessToken },
          effectiveCompanyId
        ),
      ]);

      setApplication(appRes.data?.data || null);

      setProcessors(processorsRes.data?.data || []);
    } catch (err) {
      console.error("[MidApplication] Failed to fetch data", err);
      toast({
        status: "destructive",
        title: "Failed to load application",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchData();
  }, [id]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      await makeApiRequest(
        "post",
        apiRoutes.addMidApplicationComment(id),
        { id: user.id, accessToken: user.accessToken },
        effectiveCompanyId,
        { text: newComment }
      );
      toast({ status: "success", title: "Comment added" });
      setNewComment("");
      await fetchData();
    } catch (err) {
      console.error("Failed to add comment", err);
      toast({
        status: "destructive",
        title: "Failed to add comment",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // SOUND-619: PendsPanel owns the composer state and hands back a validated
  // list. Always sends the array form — the API accepts both, and one code path
  // here means one place for the error handling.
  const handleAddPends = async (items, { suppressNotifications } = {}) => {
    if (!items?.length) return;
    setSubmitting(true);
    try {
      await makeApiRequest(
        "post",
        apiRoutes.addMidApplicationPend(id),
        { id: user.id, accessToken: user.accessToken },
        effectiveCompanyId,
        {
          pends: items,
          ...(suppressNotifications ? { suppressNotifications: true } : {}),
        }
      );
      toast({
        status: "success",
        title: items.length === 1 ? "Pend added" : `${items.length} pends added`,
        description: suppressNotifications
          ? "The merchant was not notified."
          : items.length > 1
          ? "The merchant was sent one message listing all of them."
          : undefined,
      });
      await fetchData();
    } catch (err) {
      console.error("Failed to add pends", err);
      toast({
        status: "destructive",
        title: "Failed to add pends",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolvePends = async (pendIds, { suppressNotifications } = {}) => {
    if (!pendIds?.length) return;
    setSubmitting(true);
    try {
      await makeApiRequest(
        "put",
        apiRoutes.resolveMidApplicationPends(id),
        { id: user.id, accessToken: user.accessToken },
        effectiveCompanyId,
        {
          pendIds,
          ...(suppressNotifications ? { suppressNotifications: true } : {}),
        }
      );
      toast({
        status: "success",
        title:
          pendIds.length === 1 ? "Pend resolved" : `${pendIds.length} pends resolved`,
        description: suppressNotifications ? "The merchant was not notified." : undefined,
      });
      await fetchData();
    } catch (err) {
      console.error("Failed to resolve pends", err);
      toast({
        status: "destructive",
        title: "Failed to resolve pends",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async ({ reason, suppressNotifications }) => {
    if (!selectedStatus) return;
    setSubmitting(true);
    try {
      await makeApiRequest(
        "put",
        apiRoutes.changeMidApplicationStatus(id),
        { id: user.id, accessToken: user.accessToken },
        effectiveCompanyId,
        {
          status: selectedStatus,
          reason: reason || undefined,
          // SOUND-618: only send when true, so the request body is unchanged for
          // the normal notify-the-merchant path.
          ...(suppressNotifications ? { suppressNotifications: true } : {}),
        }
      );
      toast({
        status: "success",
        title: `Status changed to ${STATUS_LABELS[selectedStatus] || selectedStatus}`,
        description: suppressNotifications ? "The merchant was not notified." : undefined,
      });
      setConfirmOpen(false);
      setSelectedStatus("");
      await fetchData();
    } catch (err) {
      console.error("Failed to change status", err);
      toast({
        status: "destructive",
        title: "Failed to change status",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <Skeleton.Rect className="h-8 w-48 mb-4" />
        <Skeleton.Rect className="h-5 w-full mb-2" />
        <Skeleton.Rect className="h-5 w-2/3 mb-2" />
      </div>
    );
  }

  if (!application) {
    return (
      <div className="p-6 text-center">
        <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
        <h3 className="mt-2 text-sm font-medium text-foreground">
          Application not found
        </h3>
      </div>
    );
  }

  // SOUND-654: the API resolves this from processorId. The local lookup stays
  // as a fallback for a cached response served before that shipped.
  const processorName =
    application.processorName ||
    processors.find((p) => p.processorId === application.processorId)?.name ||
    application.processorId;

  // SOUND-776: internal + Kurv + not yet transmitted. The API's own check is
  // `/kurv/i` on the processor name; canShowTransmit applies the same test.
  const showTransmit = canShowTransmit({
    isInternal: isAdmin,
    processorName,
    application,
  });

  return (
    <div>
      <PageHeader
        title={`Application ${application.clientName || application.companyName} <> ${processorName}`}
        subtitle="View and update this MID application"
        actions={
          <>
            <button className="btn btn-ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/companies/${application.companyId}`)}
            >
              View Registration
            </button>
            {isAdmin && (
              <button
                className="btn btn-primary"
                disabled={application.currentStatus !== "approved"}
                title={
                  application.currentStatus !== "approved"
                    ? "Application must be approved before activating a MID"
                    : "Create a MID from this application"
                }
                onClick={() =>
                  navigate(`/mids/create?applicationId=${application.applicationId}`)
                }
              >
                <CreditCard className="h-4 w-4" />
                Activate MID
              </button>
            )}
          </>
        }
      />

      {/* Matches the registration tab and the ticket detail page: a fixed
          320px rail, main column takes the remainder. */}
      <div className="container grid grid-cols-1 items-start gap-6 py-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">
        {/* Basic Info */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5 text-brand" />
            Application Details
          </h2>
          <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 lg:grid-cols-3">
            {/* SOUND-654: `companyName` holds the DBA on 140 of 161 records,
                so this field has been labelling the DBA as "Company". Both are
                resolved from keys by the API now — the client from companyId,
                the DBA from the application->dba edge — so show both, named. */}
            <InfoField label="Client" value={application.clientName} />
            <InfoField label="DBA" value={application.dbaName} />
            <InfoField label="Processor" value={processorName} />
            <InfoField
              label="Source"
              value={<SourceBadge source={application.source} />}
            />

          </div>
        </section>

        {/* Pends — SOUND-619: redesigned + extracted to its own component. */}
        <PendsPanel
          pends={application.pends || []}
          isAdmin={isAdmin}
          submitting={submitting}
          onAddPends={handleAddPends}
          onResolvePends={handleResolvePends}
        />

        {/* Comments */}
        <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-brand" />
            Comments
          </h2>
          {application.comments?.length > 0 ? (
            <ul className="space-y-3">
              {application.comments.map((c) => (
                <li
                  key={c.commentId}
                  className="text-sm text-muted-foreground border border-border rounded-lg p-3"
                >
                  <p>{c.text}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(c.createdAt).toLocaleDateString()}{" "}
                    {c.createdBy?.name
                      ? `by ${c.createdBy.name} (${c.createdBy.email})`
                      : ""}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              No comments have been added yet
            </p>
          )}

          {/* Add Comment Form */}
          <div className="mt-4 flex gap-2">
            <input
              type="text"
              className="input input-bordered flex-1"
              placeholder="Write a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            />
            <button
              className="btn btn-primary"
              onClick={handleAddComment}
              disabled={submitting}
            >
              <Plus className="h-4 w-4" />
              Add
            </button>
          </div>
        </section>

        {/* Notes */}
        {application.notes && (
          <section className="surface rounded-xl ring-1 ring-border p-6">
            <h2 className="text-lg font-semibold mb-2">Notes</h2>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {application.notes}
            </p>
          </section>
        )}
        {/* SOUND-618: shared confirmation surface (same component the
            registration tab uses, so the two cannot drift). */}
        <ApplicationStatusDialog
          open={confirmOpen && !!selectedStatus}
          targetStatus={selectedStatus}
          // Labels here are past-tense adjectives ("Declined"), so the title is
          // phrased as a status change rather than an imperative.
          title={`Change status to ${STATUS_LABELS[selectedStatus] || selectedStatus}?`}
          confirmLabel="Update Status"
          statusLabel={STATUS_LABELS[selectedStatus] || selectedStatus}
          requiresReason={REASON_REQUIRED_MID_STATUSES.has(selectedStatus)}
          terminal={(MID_TRANSITIONS[selectedStatus] || []).length === 0}
          canSuppress={isAdmin}
          submitting={submitting}
          entityNoun="MID application"
          onConfirm={handleStatusChange}
          onCancel={() => setConfirmOpen(false)}
        />

        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* SOUND-776: Kurv submission. Before transmit: the action. After:
              a read-only record of what was sent. Internal-only — the Kurv
              portal link is not for merchants or partners. */}
          {showTransmit && (
            <section className="surface p-4">
              <h3 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Send className="h-3.5 w-3.5" />
                Kurv submission
              </h3>
              <p className="mb-3 text-xs text-muted-foreground">
                Check this application against Kurv&rsquo;s requirements, pick an MCC, and submit it.
              </p>
              <button
                className="btn btn-primary w-full justify-center gap-2"
                onClick={() => setTransmitOpen(true)}
              >
                <Send className="h-4 w-4" />
                Transmit to Kurv
              </button>
            </section>
          )}
          {isAdmin && <StratusTransmittedPanel application={application} />}

          {/* Actions — status transitions only. Matches the registration tab's
              Actions panel: ticketing chrome, one outline button per legal
              transition, semantic colour carried by the pill in the menu rather
              than by a filled block. */}
          {isAdmin && (MID_TRANSITIONS[application.currentStatus] || []).length > 0 && (
            <section className="surface p-4">
              <h3 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Settings className="h-3.5 w-3.5" />
                Actions
              </h3>
              <div className="space-y-2">
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="select w-full py-1.5 text-sm"
                >
                  <option value="">Move to&hellip;</option>
                  {(MID_TRANSITIONS[application.currentStatus] || []).map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABELS[s] || s}
                    </option>
                  ))}
                </select>
                {/*
                  SOUND-618: the reason input lives in ApplicationStatusDialog, so
                  it is captured at the moment the operator confirms the (often
                  irreversible) transition.
                */}
                <button
                  className="btn btn-outline w-full justify-start gap-2"
                  onClick={() => setConfirmOpen(true)}
                  disabled={submitting || !selectedStatus}
                >
                  <Settings className="h-4 w-4" />
                  <span>Update status</span>
                </button>
              </div>
            </section>
          )}

          {/* Status Timeline — the same dot rail the ticket, registration and
              MID pages use. midApplications statusHistory is MIXED: the oldest
              rows are { status, timestamp, userId } with no from/to, newer rows
              carry both. buildStatusTimeline normalises them. */}
          <section className="surface p-4">
            <h3 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              Status Timeline
            </h3>
            <div className="mb-4 flex items-center justify-between gap-2 border-b border-border/60 pb-3">
              <span className="text-xs text-muted-foreground">Current status</span>
              {/* SOUND-688: NO pended badge here. The Pends panel on this same
                  page already leads with "3 open" — restating it next to the
                  stage adds nothing (CPO 2026-08-24). */}
              <RecordStatusPill
                status={application.currentStatus}
                toneFor={statusToneFrom(MID_APP_STATUS)}
              />
            </div>
            <RecordTimeline
              entries={buildStatusTimeline(application.statusHistory, {
                createdAt: application.createdAt,
                createdLabel: "Application created",
                labelFor: statusLabelFrom(MID_APP_STATUS),
                dotFor: statusDotFrom(MID_APP_STATUS),
              })}
            />
            {application.updatedAt && (
              <div className="mt-4 border-t border-border/60 pt-3 text-[11px] text-muted-foreground">
                Last updated {new Date(application.updatedAt).toLocaleDateString()}
              </div>
            )}
          </section>

          {/* SOUND-649: links this application holds itself — the DBA it is for,
              the MID it produced — as opposed to links made from a ticket. */}
          {isAdmin && (
            <RecordLinksPanel
              entityType="application"
              entityId={application.applicationId}
              companyId={application.companyId}
              // The panel read empty on every application, because an
              // application's relationships are all FOREIGN KEYS — companyId,
              // dbaId, merchantId — and the panel reads entityLinks. Same fix
              // as the MID record: FK-derived relationships are PINNED (they
              // cannot be unlinked, only reassigned), and real links list below.
              // ONLY ownership is pinned. companyId is the authorization
              // anchor and processor is an access-control key, so neither can
              // be unlinked — only reassigned. The DBA and any MIDs this
              // application produced are real entityLinks now (SOUND-649/655),
              // so they list below like anything else and the reverse read
              // supplies the MIDs without a second query.
              pinned={[
                application.companyId && {
                  key: "company",
                  icon: Building2,
                  // clientName, not companyName — the stored companyName holds
                  // the DBA brand on 139 of 160 applications (SOUND-654).
                  label:
                    application.clientName ||
                    application.companyName ||
                    application.companyId,
                  sublabel: "Company",
                  href: `/companies/${application.companyId}`,
                },
                processorName && {
                  key: "processor",
                  icon: Cpu,
                  label: processorName,
                  sublabel: "Processor",
                },
              ].filter(Boolean)}
            />
          )}

          {/* Linked tickets (reverse lookup — SOUND-497), internal-only.
              In the rail rather than the main column: a MID application rarely
              has its own ticket, so it does not earn full-width space above the
              workflow it belongs to. */}
          {isAdmin && (
            <EntityTicketsSection
              entityType="application"
              entityId={application.applicationId}
              variant="rail"
            />
          )}
        </div>
      </div>

      {/* Mounted at page level, not inside the action section: that section
          disappears the moment the record reads as transmitted, and the dialog
          must survive to show its result screen. */}
      {isAdmin && (
        <StratusTransmitModal
          open={transmitOpen}
          onClose={() => setTransmitOpen(false)}
          application={application}
          onTransmitted={() => fetchData({ silent: true })}
        />
      )}
    </div>
  );
}

export default requireAuth(MidApplication);
